import { useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import api, { ApiResponse } from '../axios';
import { ENDPOINTS } from '../endpoints';
import {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  AuthMeResponse,
} from '../types/auth.types';
import { useAuthStore } from '@/stores/authStore';
import { useTenantStore } from '@/stores/tenantStore';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { extractErrorMessage } from '../errors';

// Login mutation
export function useLogin() {
  const { login } = useAuthStore();
  const { setTenant } = useTenantStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (data: LoginRequest) => {
      const response = await api.post<ApiResponse<LoginResponse>>(
        ENDPOINTS.AUTH.LOGIN,
        data
      );
      return response.data.data!;
    },
    onSuccess: (data) => {
      login(data.user, data.accessToken);
      // Store tenant info for multi-tenant requests
      if (data.tenant) {
        const features = data.tenant.features || {};
        const enabledFeatures = (Object.keys(features) as (keyof typeof features)[])
          .filter(k => typeof features[k] === 'boolean' && features[k])
          .map(k => String(k));
        setTenant({
          id: data.tenant.id,
          name: data.tenant.name,
          identifier: data.tenant.subdomain,
          features: enabledFeatures,
        });
      }
      // Customers face the kiosk screen - a staff greeting there is noise.
      if (data.user.role !== 'KioskIntake') toast.success(`שלום, ${data.user.fullName}!`);
      navigate(data.user.role === 'KioskIntake' ? '/kiosk' : '/dashboard');
    },
    onError: () => {
      toast.error('שם משתמש או סיסמה שגויים');
    },
  });
}

// Register a new organization. The endpoint returns the same payload as login, so a successful
// registration signs the new admin straight in rather than bouncing them to the login screen.
// Named for the organization rather than "useRegister" - useCash already exports a useRegister that
// fetches a cash register, and the barrel file re-exports both.
export function useRegisterOrganization() {
  const { login } = useAuthStore();
  const { setTenant } = useTenantStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (data: RegisterRequest) => {
      const response = await api.post<ApiResponse<LoginResponse>>(
        ENDPOINTS.AUTH.REGISTER,
        data
      );
      return response.data.data!;
    },
    onSuccess: (data) => {
      login(data.user, data.accessToken);
      if (data.tenant) {
        setTenant({
          id: data.tenant.id,
          name: data.tenant.name,
          identifier: data.tenant.subdomain,
          features: [],
        });
      }
      toast.success(`הארגון נוצר בהצלחה. ברוך הבא, ${data.user.fullName}!`);
      navigate('/dashboard');
    },
    onError: (error) => {
      // The server distinguishes a taken email from a taken subdomain; surfacing its own message is
      // the only way the user learns which of the two to change.
      toast.error(extractErrorMessage(error, 'ההרשמה נכשלה. נסי שוב.'));
    },
  });
}

// Logout mutation
export function useLogout() {
  const { logout } = useAuthStore();
  const { clearTenant } = useTenantStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      await api.post(ENDPOINTS.AUTH.LOGOUT);
    },
    onSuccess: () => {
      logout();
      clearTenant();
      toast.success('להתראות!');
      navigate('/login');
    },
    onSettled: () => {
      // Always logout locally even if API fails
      logout();
      clearTenant();
    },
  });
}

// Get current user
export function useCurrentUser() {
  const { setUser, setLoading, token } = useAuthStore();

  const query = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const response = await api.get<ApiResponse<AuthMeResponse>>(
        ENDPOINTS.AUTH.ME
      );
      return response.data.data!;
    },
    enabled: !!token,
  });

  // Handle success/error with useEffect (React Query v5 pattern)
  useEffect(() => {
    if (query.isSuccess && query.data) {
      setUser(query.data.user);
      setLoading(false);
    }
  }, [query.isSuccess, query.data, setUser, setLoading]);

  useEffect(() => {
    if (query.isError) {
      setLoading(false);
    }
  }, [query.isError, setLoading]);

  return query;
}

// Forgot password
export function useForgotPassword() {
  return useMutation({
    mutationFn: async (email: string) => {
      await api.post(ENDPOINTS.AUTH.FORGOT_PASSWORD, { email });
    },
    onSuccess: () => {
      toast.success('קישור לאיפוס סיסמה נשלח למייל');
    },
    onError: () => {
      toast.error('שגיאה בשליחת המייל');
    },
  });
}

// Update own profile (name/phone) - self-service, works for any authenticated role
export function useUpdateOwnProfile() {
  const { user, setUser } = useAuthStore();

  return useMutation({
    mutationFn: async (updates: { firstName?: string; lastName?: string; phone?: string }) => {
      const response = await api.put<ApiResponse<{
        firstName: string;
        lastName: string;
        fullName: string;
        phone?: string;
      }>>(ENDPOINTS.AUTH.ME, updates);
      return response.data.data!;
    },
    onSuccess: (data) => {
      if (user) {
        setUser({
          ...user,
          firstName: data.firstName,
          lastName: data.lastName,
          fullName: data.fullName,
          phone: data.phone,
        });
      }
      toast.success('הפרופיל עודכן בהצלחה');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בעדכון הפרופיל'));
    },
  });
}

// Change own password - requires current password, ends other sessions on success
export function useChangeOwnPassword() {
  const { logout } = useAuthStore();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      await api.post(ENDPOINTS.AUTH.CHANGE_PASSWORD, data);
    },
    onSuccess: () => {
      toast.success('הסיסמה שונתה בהצלחה. יש להתחבר מחדש.');
      logout();
      navigate('/login');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה בשינוי הסיסמה'));
    },
  });
}

// Re-confirms the current user's own password with no other side effect (no new token, no forced
// logout) - used by the kiosk staff-logout confirmation modal. Caller decides what happens next
// (e.g. calling useLogout() on success).
export function useVerifyPassword() {
  return useMutation({
    mutationFn: async (password: string) => {
      await api.post(ENDPOINTS.AUTH.VERIFY_PASSWORD, { password });
    },
  });
}

// Reset password
export function useResetPassword() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (data: { email: string; token: string; newPassword: string }) => {
      await api.post(ENDPOINTS.AUTH.RESET_PASSWORD, data);
    },
    onSuccess: () => {
      toast.success('הסיסמה אופסה בהצלחה');
      navigate('/login');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'שגיאה באיפוס הסיסמה. ייתכן שהקישור פג תוקף.'));
    },
  });
}
